<!-- sections/ResultsSection.vue -->
<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import BaseSection from '../components/BaseSection.vue';
import { InputType } from '@/interfaces/InputFields';
import { numericAboveZero } from '@/validation';
import ConnxField, { ErrorUpdate } from '@/components/Molecules/Field/ConnxField.vue';

const INPUT_NAME = 'inputResults';

interface Props {
  results: number | string;
  errors?: Map<string, string | null>;
  showTitle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => new Map(),
  showTitle: true,
});

const emit = defineEmits<{
  'field-error': [{ name: string; error: string | null }];
  'update:results': [value: number];
}>();

const clearError = () => {
  emit('field-error', {
    name: INPUT_NAME,
    error: '',
  });
};

// Default to 1 if invalid or <= 0
const inputResults = ref<string | number>(Number(props.results) > 0 ? props.results : 5);

const resultsChoice = computed({
  get() {
    const val = Number(props.results);
    return val === -1 ? 'all' : 'specify';
  },
  set(choice: string) {
    clearError();

    if (choice === 'all') {
      emit('update:results', -1);
      return;
    }

    if (choice === 'specify') {
      const parsed = Number(inputResults.value);
      const safeValue = Number.isInteger(parsed) && parsed > 0 ? parsed : 5;

      inputResults.value = safeValue;
      emit('update:results', safeValue);
    }
  },
});

const handleFieldError = (error: ErrorUpdate) => {
  emit('field-error', error);
};

watch(inputResults, (newVal) => {
  if (typeof newVal === 'string' && newVal.trim() === '') {
    return;
  }

  const parsed = typeof newVal === 'string' ? parseInt(newVal, 10) : newVal;

  // Auto-correct invalid values (0, negatives, NaN)
  if (isNaN(parsed) || parsed <= 0) {
    inputResults.value = 1;

    if (resultsChoice.value === 'specify') {
      emit('update:results', 1);
    }
    return;
  }

  if (resultsChoice.value === 'specify') {
    emit('update:results', Math.floor(parsed));
  }
});
</script>

<template>
  <BaseSection title="Number of Results" :should-show="true" :show-title="showTitle">
    <connx-field
      name="results-choice"
      v-model="resultsChoice"
      class="tw-mb-0 tw-pt-0"
      :type="InputType.Radio"
      :options="[
        { label: 'All', value: 'all' },
        { label: 'Specify', value: 'specify' },
      ]"
      @update:error="handleFieldError"
    />

    <div class="tw-ml-9" v-if="resultsChoice === 'specify'">
      <connx-field
        :name="INPUT_NAME"
        v-model="inputResults"
        required
        :type="InputType.Text"
        :rules="numericAboveZero"
        @update:error="handleFieldError"
      />
    </div>
  </BaseSection>
</template>
